import { useState } from 'react'

import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import MenuItem from '@mui/material/MenuItem'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import DialogTitle from '@components/_mui/DialogTitle'
import Typography from '@components/_mui/Typography'

import ConfirmationV2 from '@components/Dialog/ConfirmationV2'

/**
 * Single confirmation dialog for any task status change.
 * Renders ConfirmationV2 when there's no reason to collect, otherwise a reason form.
 *
 * @param reason - undefined for a plain confirm, or { type: 'select' | 'text', label, options?, required?, multiline? }
 * @param onConfirm - called with the entered reason (undefined for the plain case)
 */
export default function ConfirmStatusChange({ open, title, description, reason, onConfirm, onCancel }) {
  if (!reason) {
    return <ConfirmationV2 open={open} title={title} description={description} onReject={onCancel} onConfirm={() => onConfirm()} />
  }

  return (
    <Dialog open={open} onClose={onCancel} fullWidth maxWidth="xs">
      <ReasonContent title={title} description={description} reason={reason} onCancel={onCancel} onConfirm={onConfirm} />
    </Dialog>
  )
}

function ReasonContent({ title, description, reason, onCancel, onConfirm }) {
  const [value, setValue] = useState('')

  const invalid = reason.required && value.trim() === ''

  return (
    <>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Stack spacing={1} sx={{ pt: description ? 0 : 1 }}>
          {description && <Typography>{description}</Typography>}
          {reason.type === 'select' ? (
            <TextField
              select
              fullWidth
              label={value ? '' : reason.label}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              sx={{ minWidth: 200 }}
              slotProps={{ inputLabel: { shrink: false } }}
            >
              {(reason.options || []).map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </TextField>
          ) : (
            <TextField
              fullWidth
              autoFocus
              multiline={reason.multiline}
              minRows={reason.multiline ? 3 : undefined}
              label={reason.label}
              value={value}
              onChange={(e) => setValue(e.target.value)}
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <Button variant="contained" disabled={invalid} onClick={() => onConfirm(value)}>
          Confirm
        </Button>
      </DialogActions>
    </>
  )
}
